import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import { RateType } from '@/enums'
import { ParserRegistration } from '@/components/Timesheet/types'
import { useTimesheetFormStore } from '@/components/Timesheet/store/useTimesheetFormStore'

interface UploadedFile {
  name: string
  localId: string
  size?: number
}

export enum ProcessStep {
  UPLOADING = 'UPLOADING',
  EXTRACTING = 'EXTRACTING',
  DELETING = 'DELETING',
  CLEAN = 'CLEAN',
  PARSED = 'PARSED',
}

export const useTimesheetParsingStore = defineStore('timesheet-parsing-store', () => {
  const timesheetFormStore = useTimesheetFormStore()

  const parsedRegistrations = ref<ParserRegistration[]>([])
  // Keep track of initial registration amounts so we can rollback when desired
  const preParsedRegistrationDurations = ref({})
  const countOfValidParsedRegistrationsToBeFilled = ref<number>(0)
  const countOfRegistrationsOutsideOfTheCurrentPeriod = ref<number>(0)
  const countOfRegistrationsWithIncompatibleRateType = ref<number>(0)
  const userClosedAlertForParsedEntriesOutsideBillingPeriod = ref(false)
  const uploadedFiles = ref<UploadedFile[]>([])
  const attachFilesToRequest = ref(true)
  const currentStep = ref<ProcessStep>(ProcessStep.CLEAN)

  const isCompatibleRateType = (registration: ParserRegistration): boolean => (
      (timesheetFormStore.rateType === RateType.DAILY && registration.unit === 'days') ||
      (timesheetFormStore.rateType === RateType.HOURLY && registration.unit === 'hours')
    )

  const isValidRegistration = (registration: ParserRegistration): boolean => {
    if (!registration.date || !registration.duration || !registration.unit || registration.type !== 'day') {
      return false
    }

    if (!isCompatibleRateType(registration)) {
      countOfRegistrationsWithIncompatibleRateType.value += 1
      return false
    }

    return true
  }

  const updateDateEntry = (date: string, duration: number): boolean => {
    const dateEntry = timesheetFormStore.tableDateEntries[date]
    if (!dateEntry || timesheetFormStore.isDateDisabled(date)) {
      countOfRegistrationsOutsideOfTheCurrentPeriod.value += 1
      return false
    }

    preParsedRegistrationDurations.value[date] = { ...dateEntry }
    dateEntry.duration = duration
    return true
  }

  const hydrateFromParserRegistrations = (registrations: ParserRegistration[]) => {
    userClosedAlertForParsedEntriesOutsideBillingPeriod.value = false
    parsedRegistrations.value = registrations
    countOfRegistrationsWithIncompatibleRateType.value = 0

    countOfValidParsedRegistrationsToBeFilled.value = registrations
      .filter(isValidRegistration)
      .filter((reg) => updateDateEntry(reg.date, reg.duration)).length
  }

  const rollbackParsedTimesheetRegistrations = () => {
    Object.keys(preParsedRegistrationDurations.value).forEach((date) => {
      timesheetFormStore.tableDateEntries[date].duration =
        preParsedRegistrationDurations.value[date]?.duration
    })
    uploadedFiles.value = []
    countOfValidParsedRegistrationsToBeFilled.value = 0
    countOfRegistrationsOutsideOfTheCurrentPeriod.value = 0
    countOfRegistrationsWithIncompatibleRateType.value = 0
    currentStep.value = ProcessStep.CLEAN
  }

  const filesToAttachToPaymentRequest = computed(() =>
    attachFilesToRequest.value ? uploadedFiles.value : [],
  )

  const operationInProgress = computed(
    () => ![ProcessStep.PARSED, ProcessStep.CLEAN].includes(currentStep.value),
  )

  return {
    hydrateFromParserRegistrations,
    rollbackParsedTimesheetRegistrations,
    preParsedRegistrationDurations,
    countOfValidParsedRegistrationsToBeFilled,
    countOfRegistrationsOutsideOfTheCurrentPeriod,
    countOfRegistrationsWithIncompatibleRateType,
    userClosedAlertForParsedEntriesOutsideBillingPeriod,
    uploadedFiles,
    attachFilesToRequest,
    filesToAttachToPaymentRequest,
    currentStep,
    ProcessStep,
    operationInProgress,
  }
})
